import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuMessageCircle } from 'react-icons/lu';
import api from '../../services/api';
import ModalShell from '../ui/ModalShell';
import ReprogramarSheet from './ReprogramarSheet';

// Visitas sin confirmar de un día (4-nov-2026). Se abre desde el aviso de las 17 h ("Mañana hay N visitas sin
// confirmar") o desde la Agenda. Cada una: Confirmar, Otro día o Liberar (con unos segundos para deshacer).
// Nunca se confirma sola.
const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const diaTxt = (iso) => { const [a, m, d] = String(iso).slice(0, 10).split('-').map(Number); return `${DIAS[new Date(a, m - 1, d).getDay()]} ${d}/${m}`; };

// WhatsApp de un celular argentino guardado como "11 7000-0000", "011…" o "+54 9 11…"
export function waNumero(tel) {
    let d = String(tel || '').replace(/\D/g, '');
    if (!d) return null;
    if (d.startsWith('54')) d = d.slice(2);
    if (d.startsWith('9')) d = d.slice(1);
    if (d.startsWith('0')) d = d.slice(1);
    return d.length >= 10 ? `549${d}` : null;
}

export function mensajeConfirmar(v) {
    const franja = v.franja && v.franja !== 'sin hora' ? ` de ${v.franja} h` : '';
    return `Hola${v.cliente ? ` ${String(v.cliente).split(' ')[0]}` : ''}! Te escribimos de Dispenser La Tienda para confirmar la visita `
        + `del ${diaTxt(v.fecha)}${franja}${v.direccion ? ` en ${v.direccion.split(',')[0]}` : ''}. ¿Te queda bien? 🙌`;
}

export function EstadoConfirmacion({ v }) {
    const tec = (v.tecnico || 'Técnico').split(' ')[0];
    const chip = 'h-6 px-2 rounded-md text-caption font-black inline-flex items-center bg-chip';
    return (
        <span className="flex flex-wrap gap-1.5">
            <span className={`${chip} text-[var(--warning-tx)]`}>⏳ Cliente</span>
            <span className={`${chip} ${v.tecnicoNoPuede ? 'text-brand-red' : v.tecnicoPuede ? 'text-[#16A34A]' : 'text-muted'}`}>
                {v.tecnicoNoPuede ? '✋' : v.tecnicoPuede ? '✓' : '…'} {tec}
            </span>
        </span>
    );
}

export default function SinConfirmarSheet({ fecha = null, onCerrar, onCambio }) {
    const [datos, setDatos] = useState(null);
    const [reprogramar, setReprogramar] = useState(null);   // { orden, tecnicos }
    const [ocultas, setOcultas] = useState([]);              // liberándose (se pueden deshacer)
    const pendientes = useRef({});

    const cargar = useCallback(() => {
        api.get('/ordenes/sin-confirmar', { params: fecha ? { fecha } : {} })
            .then(r => setDatos(r.data)).catch(() => { toast.error('No se pudieron cargar'); setDatos({ visitas: [] }); });
    }, [fecha]);
    useEffect(() => { cargar(); }, [cargar]);
    // Si se cierra con algo liberándose, se libera igual
    useEffect(() => () => { Object.values(pendientes.current).forEach(p => { clearTimeout(p.t); p.hacer(); }); }, []);

    const listo = () => { cargar(); onCambio && onCambio(); };
    const confirmar = async (v) => {
        try {
            await api.patch(`/ordenes/${v.id}/fecha-tentativa`, { sinConfirmar: false });
            toast.success(`Confirmada: le avisamos a ${v.tecnico || 'el técnico'}`);
            listo();
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo confirmar'); }
    };
    const otroDia = async (v) => {
        try {
            const [o, t] = await Promise.all([api.get(`/ordenes/${v.id}`), api.get('/ordenes/tecnicos')]);
            setReprogramar({ orden: o.data, tecnicos: t.data || [] });
        } catch { toast.error('No se pudo abrir la visita'); }
    };
    const liberar = (v) => {
        setOcultas(o => [...o, v.id]);
        const hacer = async () => {
            delete pendientes.current[v.id];
            try { await api.post(`/ordenes/${v.id}/liberar`); onCambio && onCambio(); }
            catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo liberar'); setOcultas(o => o.filter(x => x !== v.id)); }
        };
        const t = setTimeout(hacer, 5000);
        pendientes.current[v.id] = { t, hacer };
        toast((tt) => (
            <span className="flex items-center gap-3">
                <span>Franja liberada{v.pedidoId ? ': el pedido vuelve a Por agendar' : ''}</span>
                <button type="button" className="font-black text-brand-red" onClick={() => {
                    clearTimeout(t); delete pendientes.current[v.id];
                    setOcultas(o => o.filter(x => x !== v.id)); toast.dismiss(tt.id);
                }}>Deshacer</button>
            </span>
        ), { duration: 5000 });
    };

    const visitas = (datos?.visitas || []).filter(v => !ocultas.includes(v.id));
    const btn = 'flex-1 h-10 rounded-xl text-label font-black active:scale-95';
    return (
        <>
            <ModalShell titulo="Sin confirmar" subtitulo={datos ? `${diaTxt(datos.fecha)} · ${visitas.length} visita${visitas.length !== 1 ? 's' : ''}` : 'Cargando…'}
                onCerrar={onCerrar} lateral>
                <div className="space-y-2.5">
                    {datos && visitas.length === 0 && (
                        <p className="py-10 text-center text-caption text-muted">No queda nada sin confirmar para ese día. 🙌</p>
                    )}
                    {visitas.map(v => {
                        const wa = waNumero(v.clienteTelefono);
                        return (
                            <div key={v.id} className="p-3.5 rounded-2xl bg-panel border-l-4 border-[#D97706] space-y-2">
                                <div>
                                    <p className="text-body font-black text-ink">{v.cliente} · {v.tecnico || 'Sin técnico'} · {v.franja}</p>
                                    <p className="text-caption text-muted truncate">{[v.empresa ? `${v.empresa} · a coordinar por la empresa` : null, v.direccion].filter(Boolean).join(' · ')}</p>
                                </div>
                                <EstadoConfirmacion v={v} />
                                {v.tecnicoNoPuede && v.noPuedeMotivo && <p className="text-caption text-brand-red">{v.noPuedeMotivo}</p>}
                                {wa && (
                                    <a href={`https://wa.me/${wa}?text=${encodeURIComponent(mensajeConfirmar(v))}`} target="_blank" rel="noreferrer"
                                        className="w-full h-10 rounded-xl bg-[#1F7A46] text-white text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95">
                                        <LuMessageCircle size={15} /> Preguntarle por WhatsApp
                                    </a>
                                )}
                                <div className="flex gap-2">
                                    <button type="button" onClick={() => confirmar(v)} className={`${btn} bg-[#16A34A] text-white`}>Confirmar</button>
                                    <button type="button" onClick={() => otroDia(v)} className={`${btn} bg-chip text-ink`}>Otro día</button>
                                    <button type="button" onClick={() => liberar(v)} className={`${btn} bg-chip text-ink`}>Liberar</button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </ModalShell>
            {reprogramar && (
                <ReprogramarSheet orden={reprogramar.orden} tecnicos={reprogramar.tecnicos}
                    onCerrar={() => setReprogramar(null)}
                    onGuardado={() => { setReprogramar(null); listo(); }} />
            )}
        </>
    );
}
